import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { staffService, type Staff, type StaffImportSummary, type StaffPayload } from "@/lib/services";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Plus, Search, Trash2, Edit2, Upload, UserCheck, UserX } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "teacher",       label: "Teacher" },
  { value: "principal",     label: "Principal" },
  { value: "accountant",    label: "Accountant" },
  { value: "non_teaching",  label: "Non-teaching" },
];

export function StaffPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "principal";
  const qcRoot = useQueryClient();

  const { data: staff = [], isPending } = useQuery({
    queryKey: ["staff"], queryFn: () => staffService.list(),
    enabled: isAdmin,
  });
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<Staff | null>(null);
  const [creating, setCreating] = useState(false);
  const [importSummary, setImportSummary] = useState<StaffImportSummary | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const importMut = useMutation({
    mutationFn: (f: File) => staffService.importTeachers(f),
    onSuccess: (res) => {
      setImportSummary(res);
      toast.success(`Updated ${res.staff_matched_updated} staff · ${res.timetable_rows_renamed} timetable rows renamed`);
      qcRoot.invalidateQueries({ queryKey: ["staff"] });
      qcRoot.invalidateQueries({ queryKey: ["timetable"] });
    },
    onError: (e: any) => toast.error(e.message || "Import failed"),
  });

  if (!isAdmin) {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-2 text-muted-foreground">
        <div className="text-lg font-semibold">Staff directory is restricted</div>
        <div className="text-sm">Only admins can view and manage staff records.</div>
      </div>
    );
  }

  const filtered = staff.filter((s) =>
    !q || s.name.toLowerCase().includes(q.toLowerCase())
       || s.email.toLowerCase().includes(q.toLowerCase())
       || (s.employee_id || "").toLowerCase().includes(q.toLowerCase())
       || (s.designation || "").toLowerCase().includes(q.toLowerCase()),
  );

  const active = staff.filter((s) => (s.status ?? "active") === "active").length;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Staff</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {staff.length} record(s) · {active} active
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) importMut.mutate(f);
              if (fileRef.current) fileRef.current.value = "";
            }}
          />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importMut.isPending}>
            {importMut.isPending
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <Upload className="mr-2 h-4 w-4" />}
            Import Teachers
          </Button>
          <Button onClick={() => setCreating(true)}><Plus className="mr-2 h-4 w-4" /> New staff</Button>
        </div>
      </div>

      {importSummary && (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
            <div className="text-sm">
              <div className="font-semibold text-emerald-700">
                ✓ Imported {importSummary.filename ?? "teachers"}
              </div>
              <div className="text-muted-foreground">
                {importSummary.rows_read} rows read · {importSummary.staff_matched_updated} matched-updated
                {importSummary.staff_created > 0 && ` · ${importSummary.staff_created} new`}
                {" · "}{importSummary.timetable_rows_renamed} timetable slots renamed
                {importSummary.warnings.length > 0 && ` · ${importSummary.warnings.length} warning(s)`}
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setImportSummary(null)}>Dismiss</Button>
          </CardContent>
        </Card>
      )}

      <div className="relative w-full max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search name, email, employee ID, designation…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
      </div>

      <Card>
        <CardContent className="p-0">
          {isPending ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Emp ID</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Designation</TableHead>
                  <TableHead>Class</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-semibold">{s.name}</TableCell>
                    <TableCell><Badge variant="secondary">{s.employee_id || "—"}</Badge></TableCell>
                    <TableCell className="capitalize">{(s.role || "").replace("_", " ")}</TableCell>
                    <TableCell className="text-muted-foreground">{s.designation || "—"}</TableCell>
                    <TableCell>{s.class_assigned ? <Badge variant="outline">Class {s.class_assigned}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-muted-foreground tabular-nums">{s.phone || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{s.email}</TableCell>
                    <TableCell>
                      {(s.status ?? "active") === "active"
                        ? <Badge variant="success"><UserCheck className="mr-1 h-3 w-3" /> Active</Badge>
                        : <Badge variant="secondary"><UserX className="mr-1 h-3 w-3" /> Inactive</Badge>}
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(s)}><Edit2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">No staff match your filter.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {creating && <StaffDialog onClose={() => setCreating(false)} />}
      {editing && <StaffDialog staff={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

// ---------------- CREATE / EDIT DIALOG ----------------
function StaffDialog({ staff, onClose }: { staff?: Staff; onClose: () => void }) {
  const isEdit = !!staff;
  const qc = useQueryClient();
  const [f, setF] = useState<StaffPayload>({
    name: staff?.name || "",
    email: staff?.email || "",
    role: staff?.role || "teacher",
    phone: staff?.phone || "",
    subject: staff?.subject || "",
    class_assigned: staff?.class_assigned || "",
    employee_id: staff?.employee_id || "",
    designation: staff?.designation || "",
    qualification: staff?.qualification || "",
    date_of_joining: staff?.date_of_joining || "",
    date_of_birth: staff?.date_of_birth || "",
    gender: staff?.gender || "",
    address: staff?.address || "",
    blood_group: staff?.blood_group || "",
    emergency_contact: staff?.emergency_contact || "",
    status: staff?.status || "active",
  });

  const cleanPayload = (): StaffPayload => {
    const out: any = {};
    Object.entries(f).forEach(([k, v]) => {
      if (v !== undefined && v !== "") out[k] = v;
    });
    // Required fields always present
    out.name = f.name; out.email = f.email; out.role = f.role;
    return out;
  };

  const save = useMutation({
    mutationFn: () => isEdit
      ? staffService.update(staff!.id, cleanPayload())
      : staffService.create(cleanPayload()),
    onSuccess: () => {
      toast.success(isEdit ? "Staff updated" : "Staff added");
      qc.invalidateQueries({ queryKey: ["staff"] });
      onClose();
    },
    onError: (e: any) => toast.error(e.message || "Save failed"),
  });

  const remove = useMutation({
    mutationFn: () => staffService.remove(staff!.id),
    onSuccess: () => {
      toast.info("Staff removed");
      qc.invalidateQueries({ queryKey: ["staff"] });
      onClose();
    },
    onError: (e: any) => toast.error(e.message || "Delete failed"),
  });

  const setField = (k: keyof StaffPayload) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setF({ ...f, [k]: e.target.value });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit staff" : "Add staff"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Update HR details. Email cannot be reused across the system." : "Create a new staff record. They will appear in the staff directory."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Field label="Full name *"><Input value={f.name} onChange={setField("name")} /></Field>
          <Field label="Employee ID"><Input value={f.employee_id} onChange={setField("employee_id")} placeholder="LFS-001" /></Field>
          <Field label="Status">
            <div className="mt-1 flex gap-1">
              {["active", "inactive"].map((s) => (
                <Button key={s} size="sm" type="button"
                  variant={f.status === s ? "default" : "outline"}
                  onClick={() => setF({ ...f, status: s })} className="capitalize">{s}</Button>
              ))}
            </div>
          </Field>

          <Field label="Email *"><Input type="email" value={f.email} onChange={setField("email")} /></Field>
          <Field label="Phone"><Input value={f.phone} onChange={setField("phone")} placeholder="+91 9..." /></Field>
          <Field label="Emergency contact"><Input value={f.emergency_contact} onChange={setField("emergency_contact")} /></Field>

          <Field label="Role *">
            <div className="mt-1 flex flex-wrap gap-1">
              {ROLES.map((r) => (
                <Button key={r.value} size="sm" type="button"
                  variant={f.role === r.value ? "default" : "outline"}
                  onClick={() => setF({ ...f, role: r.value })}>{r.label}</Button>
              ))}
            </div>
          </Field>
          <Field label="Designation"><Input value={f.designation} onChange={setField("designation")} placeholder="Class Teacher" /></Field>
          <Field label="Subject"><Input value={f.subject} onChange={setField("subject")} placeholder="Mathematics" /></Field>
          <Field label="Class teacher of">
            <Input value={f.class_assigned} onChange={setField("class_assigned")}
              placeholder="e.g. 5 or JKG" />
            <p className="mt-1 text-[10px] text-muted-foreground">
              Setting this grants the staff edit access to that class&apos;s students.
            </p>
          </Field>

          <Field label="Qualification"><Input value={f.qualification} onChange={setField("qualification")} placeholder="M.A. B.Ed" /></Field>
          <Field label="Date of joining"><Input type="date" value={f.date_of_joining} onChange={setField("date_of_joining")} /></Field>
          <Field label="Date of birth"><Input type="date" value={f.date_of_birth} onChange={setField("date_of_birth")} /></Field>

          <Field label="Gender">
            <div className="mt-1 flex gap-1">
              {["male", "female", "other"].map((g) => (
                <Button key={g} size="sm" type="button"
                  variant={f.gender === g ? "default" : "outline"}
                  onClick={() => setF({ ...f, gender: g })} className="capitalize">{g}</Button>
              ))}
            </div>
          </Field>
          <Field label="Blood group"><Input value={f.blood_group} onChange={setField("blood_group")} placeholder="O+" /></Field>
          <Field label="" />

          <div className="col-span-full">
            <Label>Address</Label>
            <Textarea value={f.address} onChange={setField("address")} rows={2} />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {isEdit && (
            <Button variant="ghost" className="mr-auto text-destructive hover:bg-red-50"
              onClick={() => { if (confirm(`Delete ${staff!.name}? This cannot be undone.`)) remove.mutate(); }}
              disabled={remove.isPending}>
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()}
            disabled={save.isPending || !f.name || !f.email || !f.role}>
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save changes" : "Add staff"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children?: React.ReactNode }) {
  return (
    <div>
      {label && <Label>{label}</Label>}
      {children}
    </div>
  );
}
